import React, { useState, useRef, useContext, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Spin, Grid, Select } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import "./AttendanceCalendar.css";

export default function LeaveCalendar({ employee }) {
    const { authData, backUrl } = useContext(AuthContext);
    const calendarRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const MIN_YEAR = 2026;
    const MIN_MONTH = 2; // November (1-based, because FullCalendar month = getMonth()+1)


    const [currentMonth, setCurrentMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();

    const leaveDataRef = useRef({});

    console.log("Employee : ", employee)

    const employeeId =
        employee?.employee?.id ||
        employee?.id ||
        authData?.employee?.id ||
        null;

    /* ---------------------------------------------- */
    /* YEAR OPTIONS                                    */
    /* ---------------------------------------------- */
    const yearOptions = Array.from({ length: 6 }, (_, i) => selectedYear - 3 + i)
        .filter((y) => y >= MIN_YEAR);

    /* ---------------------------------------------- */
    /* STATUS COLORS                                   */
    /* ---------------------------------------------- */
    const statusColors = {
        PRESENT: "green",
        ABSENT: "red",
        LEAVE: "red",
        HALF_DAY_LEAVE: "#ff9999",
        HALFDAYLEAVE: "#ff9999",
        PERMISSION: "lightgreen",
        HOLIDAY: "orange",
        SUNDAY: "yellow",
        COMPOFF: "blue",
        DEFAULT: "white",
        WEEKOFF: 'yellow',
        LOP: "red",
        TECHNICAL_ISSUE: "gray",
    };

    const getDarkerColor = (hex) => {
        const amt = -60;
        let usePound = false;
        if (hex[0] === "#") {
            hex = hex.slice(1);
            usePound = true;
        }
        let num = parseInt(hex, 16);
        let r = (num >> 16) + amt;
        let g = ((num >> 8) & 0x00ff) + amt;
        let b = (num & 0x0000ff) + amt;
        r = Math.max(Math.min(255, r), 0);
        g = Math.max(Math.min(255, g), 0);
        b = Math.max(Math.min(255, b), 0);
        return (
            (usePound ? "#" : "") +
            ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")
        );
    };

    /* ---------------------------------------------- */
    /* LOAD LEAVES                                     */
    /* ---------------------------------------------- */
    const loadLeaves = async (month, year) => {
        if (!employeeId) return;
        const key = `${year}-${month}`;
        if (leaveDataRef.current[key]) return;

        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/calendar/?employee_id=${employeeId}&month=${month}&year=${year}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            leaveDataRef.current[key] = res.data.results || res.data || [];
        } catch (err) {
            console.error("Error fetching leave data:", err);
            leaveDataRef.current[key] = [];
        } finally {
            setLoading(false);
        }
    };

    /* ---------------------------------------------- */
    /* BUILD EVENTS                                    */
    /* ---------------------------------------------- */
    const buildEvents = () => {
        const events = [];
        Object.entries(leaveDataRef.current).forEach(([key, records]) => {
            records.forEach((item) => {
                const date = item?.Date || item?.date;
                const status = item?.AttendanceStatus?.toUpperCase?.() || "DEFAULT";
                const leaveType = item?.LeaveType || null;

                events.push({
                    start: date,
                    display: "background",
                    backgroundColor: statusColors[status] || statusColors.DEFAULT,
                    borderColor: getDarkerColor(statusColors[status] || statusColors.DEFAULT),
                    extendedProps: {
                        status,
                        secondary:
                            status === "LEAVE" ||
                                status === "HALFDAYLEAVE" ||
                                status === "HALF_DAY_LEAVE"
                                ? leaveType
                                : null,
                    },

                });
            });
        });
        return events;
    };

    /* ---------------------------------------------- */
    /* EVENT CONTENT                                   */
    /* ---------------------------------------------- */
    const eventContent = (eventInfo) => {
        const { status, secondary } = eventInfo.event.extendedProps;

        const displayText = {
            PRESENT: "Present",
            ABSENT: "Absent",
            LEAVE: "Leave",
            HALF_DAY_LEAVE: "Half",
            HALFDAYLEAVE: "Half",
            PERMISSION: "Perm",
            HOLIDAY: "Holiday",
            SUNDAY: "WeekOff",
            COMPOFF: "CompOff",
            WEEKOFF: "Weekoff",
            LOP: "LOP",
            TECHNICAL_ISSUE: "TI"
        }[status] || "-";

        return (
            <div className="fc-event-wrapper">
                <div
                    className="fc-event-top"
                    style={{ backgroundColor: getDarkerColor(statusColors[status] || statusColors.DEFAULT), opacity: 0.7 }}
                ></div>
                <div className="fc-event-bottom">
                    <span className="primary">
                        {secondary
                            ? `${displayText} (${secondary})`
                            : displayText}
                    </span>

                </div>
            </div>
        );
    };

    /* ---------------------------------------------- */
    /* HANDLE MONTH VIEW CHANGES                       */
    /* ---------------------------------------------- */
    const handleDatesSet = (info) => {
        const start = info.view.currentStart;
        const newMonth = start.getMonth() + 1; // 1-based
        const newYear = start.getFullYear();

        const api = calendarRef.current?.getApi();

        /* ---- BLOCK NAVIGATION BEFORE NOVEMBER 2025 ---- */
        if (
            newYear < MIN_YEAR ||
            (newYear === MIN_YEAR && newMonth < MIN_MONTH)
        ) {
            // Force calendar back to Nov 2025
            api.gotoDate(`${MIN_YEAR}-${String(MIN_MONTH).padStart(2, "0")}-01`);
            return;
        }

        // Update selected year if changed
        if (newYear !== selectedYear) {
            setSelectedYear(newYear);
        }

        setCurrentMonth(newMonth);

        // Load data if not cached
        const key = `${newYear}-${newMonth}`;
        if (!leaveDataRef.current[key]) {
            loadLeaves(newMonth, newYear);
        }
    };


    /* ---------------------------------------------- */
    /* INITIAL LOAD                                    */
    /* ---------------------------------------------- */
    useEffect(() => {
        if (!employeeId) return;

        const today = new Date();
        const m = today.getMonth() + 1;

        setCurrentMonth(m);
        loadLeaves(m, selectedYear);
    }, [employeeId]);

    /* ---------------------------------------------- */
    /* RENDER                                          */
    /* ---------------------------------------------- */
    return (
        <center>
            <div
                className={`leave-calendar-container ${screens.xs ? "xs" : screens.sm ? "sm" : "lg"
                    }`}
            >
                {/* ------------------ YEAR FILTER ------------------ */}
                <div style={{ marginBottom: 12 }}>
                    <label>YEAR : </label>
                    <Select
                        value={selectedYear}
                        style={{ width: 130 }}
                        onChange={(year) => {
                            setSelectedYear(year);

                            const api = calendarRef.current.getApi();
                            const safeMonth = currentMonth || new Date().getMonth() + 1;

                            // Navigate calendar to selected year
                            api.gotoDate(`${year}-${safeMonth}-01`);

                            // Load data for the new year/month
                            const key = `${year}-${safeMonth}`;
                            if (!leaveDataRef.current[key]) {
                                loadLeaves(safeMonth, year);
                            }
                        }}
                    >
                        {yearOptions.map((y) => (
                            <Select.Option key={y} value={y}>
                                {y}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                {/* ------------------ CALENDAR ------------------ */}
                {loading && !currentMonth ? (
                    <div className="leave-calendar-loading">
                        <Spin tip="Loading Leave Calendar..." />
                    </div>
                ) : (
                    <>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: screens.xs ? "prev,next" : "prev,next today",
                                center: "title",
                                right: "",
                            }}
                            height="100%"
                            events={buildEvents()}
                            eventContent={eventContent}
                            eventDisplay="block"
                            displayEventTime={false}
                            datesSet={handleDatesSet}
                        />
                        {/* ---------- LEGEND SECTION ---------- */}
                        <div
                            style={{
                                marginTop: 20,
                                padding: "10px 15px",
                                background: "#ffffffff",
                                borderRadius: 8,
                                width: "100%",
                                maxWidth: 600,
                                textAlign: "left",
                                boxShadow: "0 0 8px rgba(0,0,0,0.1)"
                            }}
                        >
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                rowGap: 8,
                                columnGap: 10
                            }}>
                                {[
                                    { label: "Present", color: "green" },
                                    { label: "Leave / LOP", color: "red" },
                                    { label: "Half Day Leave", color: "#ff9999" },
                                    { label: "Permission", color: "lightgreen" },
                                    { label: "Holiday", color: "orange" },
                                    { label: "Week-Off", color: "yellow" },
                                    { label: "Comp-Off", color: "blue" },
                                ].map((item) => (
                                    <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span
                                            style={{
                                                width: 18,
                                                height: 18,
                                                background: item.color,
                                                border: "1px solid #666",
                                                display: "inline-block"
                                            }}
                                        ></span>
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </center>
    );
}